'use client';

import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  Paper,
  CircularProgress,
  Alert,
  ClickAwayListener,
  Typography
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { apiClient } from '../../services/api';

interface Indicator {
  id: number;
  indicator_name: string;
  level1_name: string;
  level2_name: string;
  indicator_code?: string;
}

interface Chapter {
  id: number;
  title: string;
  chapter_code: string;
  writing_sequence_order: number;
}

interface LoadingState {
  [modelId: string]: boolean;
}

interface MessageInputProps {
  userInput: string;
  handleInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleSendMessage: () => void;
  handleKeyPress: (event: React.KeyboardEvent) => void;
  loading: LoadingState;
  selectedModels: string[];
  isConnected: boolean;
  projectId?: number;
  projectType?: string;
}

const MessageInput: React.FC<MessageInputProps> = ({
  userInput,
  handleInputChange,
  handleSendMessage,
  handleKeyPress,
  loading,
  selectedModels,
  isConnected,
  projectId,
  projectType
}) => {
  const [quickInputOpen, setQuickInputOpen] = useState(false);
  const [quickInputType, setQuickInputType] = useState<'indicator' | 'chapter'>('indicator');
  const [triggerText, setTriggerText] = useState('');
  const [cursorPosition, setCursorPosition] = useState(0);
  const [quickInputItems, setQuickInputItems] = useState<Indicator[] | Chapter[]>([]);
  const [loadingQuickInput, setLoadingQuickInput] = useState(false);
  const [quickInputError, setQuickInputError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const isDisabled = !isConnected || selectedModels.length === 0;
  const currentModelLoading = selectedModels.length > 0 ? loading[selectedModels[0]] : false;
  // 修复：只有在非加载状态且有输入内容时才允许发送
  const isSendDisabled = isDisabled || currentModelLoading || !userInput.trim();

  // 获取数据函数（只有重点项目才启用）
  const fetchQuickInputData = useCallback(async (type: 'indicator' | 'chapter') => {
    if (!projectId || projectType !== 'key_project') return;

    setLoadingQuickInput(true);
    setQuickInputError(null);

    try {
      let response;

      if (type === 'indicator') {
        response = await apiClient.indicators.getIndicators(projectId);
      } else {
        response = await apiClient.templateChapters.getProjectTemplateChapters(projectId);
      }

      if (response.success && response.data) {
        // 处理新的响应格式：data.chapters
        if (type === 'chapter' && Array.isArray(response.data.chapters)) {
          setQuickInputItems(response.data.chapters);
        } else if (type === 'indicator' && Array.isArray(response.data)) {
          setQuickInputItems(response.data);
        } else {
          setQuickInputError('数据格式错误');
        }
      } else {
        setQuickInputError(response.message || '获取数据失败');
      }
    } catch (err) {
      console.error('获取数据失败:', err);
      setQuickInputError('获取数据失败，请稍后重试');
    } finally {
      setLoadingQuickInput(false);
    }
  }, [projectId, projectType]);

  // 检查是否应该触发快捷输入（只有重点项目才启用）
  const checkTriggerSymbol = useCallback((text: string, position: number) => {
    // 只有 key_project 类型才启用快捷输入功能
    if (projectType !== 'key_project') return null;
    
    if (position === 0) return null;

    // 获取光标前一个字符（刚输入的字符）
    const currentChar = text.charAt(position - 1);

    if (currentChar === '@' || currentChar === '/') {
      // 移除位置限制，允许在任何位置触发
      return {
        trigger: currentChar,
        position: position - 1, // 触发符号的位置
        text: ''
      };
    }

    return null;
  }, [projectType]);

  // 处理键盘事件
  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (!isSendDisabled) {
        handleSendMessage();
      }
      return;
    }
  }, [isSendDisabled, handleSendMessage]);

  // 处理输入变化
  const handleInputChangeWithTrigger = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;

    // 先调用原有的onChange处理
    if (handleInputChange) {
      handleInputChange(event);
    }

    // 检查是否需要触发快捷输入
    const input = event.target as HTMLInputElement;
    const position = input.selectionStart || 0;

    const triggerInfo = checkTriggerSymbol(newValue, position);

    if (triggerInfo) {
      const type = triggerInfo.trigger === '@' ? 'indicator' : 'chapter';
      setQuickInputType(type);
      setTriggerText(triggerInfo.trigger);
      setCursorPosition(triggerInfo.position);
      setQuickInputOpen(true);
      // 获取数据
      fetchQuickInputData(type);
    }
  }, [handleInputChange, checkTriggerSymbol]);

  // 处理选择完成
  const handleQuickInputSelect = useCallback((item: any) => {
    if (!inputRef.current) return;

    // 获取当前光标位置
    const currentCursorPosition = inputRef.current.selectionStart || 0;

    // 找到触发符号的位置和当前输入的文本
    const beforeTrigger = userInput.substring(0, cursorPosition);
    const afterCursor = userInput.substring(currentCursorPosition);

    // 生成插入文本（保留触发符号）
    let insertText = '';
    if (quickInputType === 'indicator') {
      insertText = '@' + item.indicator_name;
    } else {
      insertText = '/' + item.title;
    }

    const newText = beforeTrigger + insertText + ' ' + afterCursor;

    // 更新输入值
    if (handleInputChange) {
      const syntheticEvent = {
        target: {
          value: newText
        }
      } as React.ChangeEvent<HTMLInputElement>;

      handleInputChange(syntheticEvent);
    }

    // 设置光标位置到插入文本之后
    setTimeout(() => {
      if (inputRef.current) {
        const newCursorPosition = beforeTrigger.length + insertText.length + 1;
        inputRef.current.setSelectionRange(newCursorPosition, newCursorPosition);
        inputRef.current.focus();
      }
    }, 0);

    // 关闭下拉列表
    setQuickInputOpen(false);
    setQuickInputItems([]);
  }, [userInput, cursorPosition, quickInputType, handleInputChange]);

  // 关闭下拉列表
  const handleQuickInputClose = useCallback(() => {
    setQuickInputOpen(false);
    setQuickInputItems([]);
    setQuickInputError(null);
  }, []);

  // 获取按钮禁用原因的提示文本
  const getDisabledTooltip = () => {
    if (!isConnected) return '网络连接中断，请稍后重试';
    if (selectedModels.length === 0) return '请先选择一个AI模型';
    if (currentModelLoading) return 'AI正在回复中，请稍等...';
    if (!userInput.trim()) return '请输入消息内容';
    return '';
  };

  const handleSend = () => {
    if (!isSendDisabled) {
      handleSendMessage();
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
      <Box sx={{ position: 'relative', flex: 1 }}>
        {/* 文本输入框 */}
        <TextField
          fullWidth
          variant="outlined"
          inputRef={inputRef}
          placeholder={
            isDisabled
              ? (!isConnected ? '正在连接服务器...' : '请选择一个模型')
              : projectType === 'key_project'
                ? '输入你的消息... (使用 @ 选择指标，使用 / 选择章节)'
                : '输入你的消息...'
          }
          value={userInput}
          onChange={handleInputChangeWithTrigger}
          onKeyDown={handleKeyDown}
          disabled={isDisabled}
          multiline
          maxRows={4}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              borderTopRightRadius: quickInputOpen ? 0 : 2,
              borderTopLeftRadius: quickInputOpen ? 0 : 2,
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main',
              },
            },
            '& .MuiOutlinedInput-input': {
              resize: 'none',
              lineHeight: 1.5,
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: quickInputOpen ? 'primary.main' : 'divider',
            },
          }}
          InputProps={{
            sx: {
              pr: 1,
            }
          }}
        />

        {/* 快捷输入下拉列表 - 显示在输入框上方 */}
        {quickInputOpen && (
          <ClickAwayListener onClickAway={handleQuickInputClose}>
            <Paper
              elevation={4}
              sx={{
                position: 'absolute',
                bottom: '100%',
                left: 0,
                right: 0,
                maxHeight: 250,
                overflow: 'auto',
                border: '2px solid',
                borderColor: 'primary.main',
                borderRadius: 2,
                boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
                zIndex: 1000,
                mb: '-1px' // 与输入框无缝连接
              }}
            >
            {/* 标题栏 */}
            <Box sx={{
              p: 2,
              borderBottom: '1px solid',
              borderColor: 'primary.light',
              bgcolor: 'primary.50',
              position: 'sticky',
              top: 0,
              zIndex: 1
            }}>
              <Typography variant="subtitle2" color="primary.dark" fontWeight="bold">
                {quickInputType === 'indicator' ? '选择指标' : '选择章节'} ({triggerText})
              </Typography>
            </Box>

            {/* 加载状态 */}
            {loadingQuickInput && (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <CircularProgress size={24} />
                <Typography sx={{ ml: 2, color: 'text.secondary' }}>
                  加载中...
                </Typography>
              </Box>
            )}

            {/* 错误状态 */}
            {quickInputError && (
              <Box sx={{ p: 2 }}>
                <Alert severity="error" sx={{ mb: 0 }}>
                  {quickInputError}
                </Alert>
              </Box>
            )}

            {/* 选项列表 */}
            {!loadingQuickInput && !quickInputError && (
              <List sx={{ py: 0 }}>
                {quickInputItems.length === 0 ? (
                  <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography color="text.secondary">
                      暂无数据
                    </Typography>
                  </Box>
                ) : (
                  quickInputItems.map((item: any) => (
                    <ListItem
                      key={item.id}
                      button
                      onClick={() => handleQuickInputSelect(item)}
                      sx={{
                        py: 1.5,
                        px: 2,
                        '&:hover': {
                          bgcolor: 'primary.50'
                        },
                        '&.Mui-selected': {
                          bgcolor: 'primary.100'
                        }
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography variant="body2" fontWeight="medium">
                            {quickInputType === 'indicator'
                              ? item.indicator_code
                                ? `${item.indicator_code} ${item.indicator_name}`
                                : item.indicator_name
                              : `${item.chapter_code} ${item.title}`
                            }
                          </Typography>
                        }
                        secondary={
                          quickInputType === 'indicator'
                            ? `${item.level1_name || ''} ${item.level2_name ? `/ ${item.level2_name}` : ''}`.trim() || '无分类'
                            : ''
                        }
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                  ))
                )}
              </List>
            )}
          </Paper>
      </ClickAwayListener>
    )}
      </Box>

      {/* 发送按钮 */}
      <Tooltip title={isSendDisabled ? getDisabledTooltip() : ''} arrow>
        <span>
          <Button
            variant="contained"
            color="primary"
            endIcon={<SendIcon />}
            onClick={handleSend}
            disabled={isSendDisabled}
            sx={{
              minWidth: '150px',
              height: '56px',
              borderRadius: 2,
              textTransform: 'none',
              '&:disabled': {
                bgcolor: 'action.disabledBackground',
                color: 'text.disabled'
              }
            }}
          >
            {currentModelLoading ? '回复中...' : '发送'}
          </Button>
        </span>
      </Tooltip>
    </Box>
  );
};

export default MessageInput;